import type { Employment, Posting, StatedPay } from "@/lib/api/catalogue";
import type { Application } from "@/lib/api/envoy";
import { SOURCE_NAME } from "@/lib/applications";
import { payLabel, WORK_SETTING_LABEL } from "@/lib/openings";
import { dayOf, postedStamp, stamp } from "./dates";
import { placeLine } from "./places";
import { applicationChip } from "./status";
import { sentPhrase, type Sent } from "./written";

/** The posting's facts: how it is worked, the pay, the description. */

export interface Fact {
    label: string;
    value: string;
    mono?: boolean;
}

const EMPLOYMENT_PHRASE: Readonly<Record<Employment, string>> = {
    FULL_TIME: "Full time",
    PART_TIME: "Part time",
    CONTRACT: "Contract",
    INTERNSHIP: "Internship",
    TEMPORARY: "Temporary",
};

/** How the job is held. Null when nothing is stated, or nothing this page has a word for. */
function employmentPhrase({ employment }: Posting): string | null {
    return employment && Object.hasOwn(EMPLOYMENT_PHRASE, employment)
        ? EMPLOYMENT_PHRASE[employment]
        : null;
}

/** How it is worked, and who said so: the posting's own field or the model's read of the text. */
export function workedPhrase(posting: Posting): string {
    if (posting.work_setting)
        return `${WORK_SETTING_LABEL[posting.work_setting]}, from the posting`;
    const read = posting.reading?.work_setting;
    if (read) return `${WORK_SETTING_LABEL[read]}, read from the text`;
    return "Not stated";
}

/** The pay, and who said so. Null when nothing is stated. */
export function payPhrase(posting: Posting): string | null {
    const stated = statedPay(posting.pay);
    if (stated) return `${stated}, stated`;
    const read = statedPay(posting.reading?.pay ?? null);
    return read ? `${read}, read from the text` : null;
}

function statedPay(pay: StatedPay | null, compact = false): string | null {
    return pay && (pay.min !== null || pay.max !== null) ? payLabel(pay, compact) : null;
}

/**
 * The pay as the header line says it: "$170,000 to $210,000", a year
 * going without saying, or "$50 to $60/hr". Compact, as a card's chip
 * says it: "$170k to $210k".
 */
export function payLine(posting: Posting, compact = false): string | null {
    return statedPay(posting.pay ?? posting.reading?.pay ?? null, compact);
}

/** The facts card, in the design's order. */
export function postingFacts(
    posting: Posting,
    application: Application | null,
    sent: Sent | null,
    timeZone?: string,
): Fact[] {
    const chip = applicationChip(application, posting);
    const pay = payPhrase(posting);
    const employment = employmentPhrase(posting);
    const status =
        application?.ready_at && application.status === "NEEDS_YOU"
            ? `${chip.label}, tailored ${dayOf(application.ready_at, timeZone)}`
            : chip.label;
    const rows: (Fact | null)[] = [
        { label: "Source", value: SOURCE_NAME[posting.source] },
        { label: "External id", value: posting.external_id, mono: true },
        { label: "Location", value: placeLine(posting) ?? "Not stated" },
        { label: "Remote", value: workedPhrase(posting) },
        employment ? { label: "Employment", value: employment } : null,
        pay ? { label: "Pay", value: pay } : null,
        {
            label: "Posted",
            value: posting.posted_at ? postedStamp(posting.posted_at, timeZone) : "Not stated",
        },
        { label: "First seen", value: stamp(posting.first_seen_at, true, timeZone) },
        posting.closed_at
            ? { label: "Closed", value: stamp(posting.closed_at, true, timeZone) }
            : { label: "Last seen", value: stamp(posting.last_seen_at, true, timeZone) },
        sent
            ? { label: "Applied", value: sentPhrase(sent, timeZone, true) }
            : { label: "Status", value: status },
        sent?.resumeId
            ? { label: "Resume id", value: sent.resumeId, mono: true }
            : { label: "Posting id", value: posting.posting_id, mono: true },
    ];
    return rows.filter((row): row is Fact => row !== null);
}
